/**
 * **页面编辑器** —— 打印包的第一个真货（`EditorContribution.component`）。
 *
 * ## 它是什么：节点图的一个**视图**，不是另一个存储
 *
 * ```text
 *   print.page 节点 ──items.N──► print.component.* 节点
 *        ▲                              ▲
 *        └──── 这个组件读它们、也写它们 ──┘
 *              （读写都走宿主给的 `doc`）
 * ```
 *
 * 「节点里改一个数 → 纸上的框跟着动；纸上拖一下 → 节点参数更新」
 * 靠的不是两处同步，而是**只有一份数据**（D2）。所以这里**没有**
 * 任何本地副本：`pages` 是从 `doc` 现算的 computed。
 *
 * ## 为什么写成 `.ts` + `h()` 而不是 `.vue`
 *
 * 装配点 `playground/nodeTypes.ts` 会被一堆 esbuild 脚本打包，而那条
 * 路径上没有 SFC 编译器（夹具包的文件头记着这件事）。真要以 npm 包分发时
 * 可以改回 `.vue`，但那样这些脚本就得先有 vue 插件。
 *
 * ## 交互：指针事件在**窗口**上收，不在元素上
 *
 * 拖动时指针经常会离开那个小框（拖快了就出界）。所以：
 * `pointerdown` 落在元素 / 把手上 → 记下起手状态 → 把 `pointermove` /
 * `pointerup` 挂到 `window` 上，松手摘掉。
 *
 * ⚠️ 试过用 `setPointerCapture`（更"现代"）：一是 happy-dom 里没有它，
 * 二是**捕获之后元素被移除（撤销 / 删除）事件就再也回不来了** ——
 * 表现是"拖到一半元素没了，之后鼠标像被粘住"。window 那套没有这个问题。
 *
 * ⚠️ **键盘一律不碰**：方向键微调 / Delete 都归宿主（`verify:layers`
 * 有一条断言盯着"画布不监听键盘"）。包的窗口也不例外 —— 桌面端方向键
 * 属于宿主选中的那个节点，抢过来会和节点图打架。
 */
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref, type PropType, type VNode } from 'vue'
import type { EditorHostProps } from '@/types/editor'
import { cls, PRINT_EDITOR_CLASS } from './classNames'
import { ensurePrintStyles } from './styles'
import { normalizeJob, type PrintJob } from './job'
import {
  activePageOf,
  DOCUMENT_TYPE,
  documentNodeOf,
  lineHeightOf,
  toLocalDelta,
  clampBoxToPage,
  BARCODE_TYPE,
  QRCODE_TYPE,
  IMAGE_TYPE,
  resizeBarcode,
  type BarcodeView,
  fitZoom,
  mmToPx,
  nextItemsHandle,
  nextPagesHandle,
  pagesOf,
  PAGE_TYPE,
  pxPerMm,
  pxToMm,
  resizeBox,
  rulerStepMm,
  rulerTicks,
  snapBox,
  snapResize,
  snapTargetsFor,
  type ElementBox,
  type ElementView,
  type PageView,
  type ResizeHandle,
  type SnapGuide,
  TEXT_TYPE,
  BOX_TYPE,
  RESIZE_HANDLES,
  MAX_ZOOM,
  MIN_ZOOM,
} from './pageModel'
import {
  compilePage,
  compileToFile,
  dotZoom,
  problemCounts,
  type PageRenderTree,
  type PrintDrawOp,
} from './renderTree'
import { createStripScroll } from '@/layout/stripScroll'
import { GRID_CHOICES, SNAP_PX, viewStateOf } from './viewState'
import { barsPathData, matrixPathData } from './pathData'
import { clamp } from './units'

/* ── 拖拽现场（不是响应式的：它每帧都在变，进响应式只是白花开销）── */
interface DragState {
  mode: 'move' | 'resize'
  id: string
  /** 这一格是不是条码：缩放要写**参数**而不是宽高（见 `applyBarcodeResize`） */
  barcode: boolean
  handle: ResizeHandle | null
  /** 起手时的指针位置（屏幕 px） */
  fromPx: { x: number; y: number }
  /** 起手时那个元素的框（mm）—— 位移从它算起，**不是逐帧累加** */
  fromBox: ElementBox
  /** 起手时那个元素的旋转角（位移要换算进它的坐标系，见 `toLocalDelta`） */
  rotationDeg: number
  page: PageView
}

/** 一排按钮（工具栏和空状态都用） */
function button(
  label: string,
  action: string,
  onRun: () => void,
  options: {
    active?: boolean
    accent?: boolean
    danger?: boolean
    title?: string
    /** 24×24 viewBox 的 stroke 路径 —— 给了就画**图标按钮**（真机反馈：按钮做成图标） */
    icon?: string
  } = {},
): VNode {
  return h(
    'button',
    {
      class: [
        'npp-btn',
        options.icon ? 'npp-btn--icon' : '',
        options.accent ? 'npp-btn--accent' : '',
        options.danger ? 'npp-btn--danger' : '',
      ],
      type: 'button',
      /* ⚠️ 图标按钮**必须有 title + aria-label**：只有图形的按钮对读屏是一片空白 */
      title: options.title ?? label,
      'aria-label': options.title ?? label,
      'data-npp-action': action,
      ...(options.active === undefined ? {} : { 'data-active': String(options.active) }),
      onClick: onRun,
    },
    options.icon
      ? [
          h(
            'svg',
            {
              width: 14,
              height: 14,
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              'stroke-width': 2,
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              'aria-hidden': 'true',
            },
            [h('path', { d: options.icon })],
          ),
        ]
      : label,
  )
}

/**
 * **二值化阈值滤镜的 id**（编辑视图与精确预览共用一份定义）。
 *
 * 定义在**文档级**的一个隐藏 `<svg>` 里（见 `ensureThresholdFilters`），
 * 两处引用同一个 id —— 各画一份滤镜的话，两张图会不一致。
 */
function thresholdFilterId(threshold: number): string {
  const level = Math.min(1, Math.max(0, Math.round(threshold) / 255))
  return `npp-threshold-${Math.round(level * 255)}`
}

/** 二值化滤镜的节点（亮度 → 离散两档；`threshold` 就是分界 0–255） */
function thresholdFilterNode(id: string, threshold: number): VNode {
  const level = Math.min(1, Math.max(0, Math.round(threshold) / 255))
  return h('filter', { id, key: id, colorInterpolationFilters: 'sRGB' }, [
    /* 先取亮度（人眼权重：绿最亮、蓝最暗），再把它压成"黑 / 白"两档 */
    h('feColorMatrix', {
      type: 'matrix',
      values:
        '0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0 0 0 1 0',
    }),
    h('feComponentTransfer', {}, [
      h('feFuncR', { type: 'discrete', tableValues: `${level} 1` }),
      h('feFuncG', { type: 'discrete', tableValues: `${level} 1` }),
      h('feFuncB', { type: 'discrete', tableValues: `${level} 1` }),
    ]),
  ])
}

/** 工具栏图标的路径（24×24 stroke）—— 风格和内核那几个图标一致 */
const TOOL_ICONS = {
  text: 'M5 6h14M12 6v13',
  box: 'M4 5h16v14H4z',
  barcode: 'M4 5v14M8 5v14M11 5v10M15 5v14M19 5v10',
  qrcode: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v6h-6v-2',
  image: 'M4 5h16v14H4zM4 15l4-4 3 3 3-3 6 6M9 9.5h.01',
} as const

const PrintPageEditor = defineComponent({
  name: 'PrintPageEditor',
  props: {
    area: { type: Object as PropType<EditorHostProps['area']>, required: true },
    doc: { type: Object as PropType<EditorHostProps['doc']>, required: true },
    /*
     * ⚠️ 这两个都是**可选**的宿主能力（见 `EditorHostProps`）：
     * 不传时编辑器照样工作（问题浮层还在），只是没有"统一输出窗口"那条出口。
     */
    publishDiagnostics: {
      type: Function as PropType<EditorHostProps['publishDiagnostics']>,
      default: undefined,
    },
    openOutput: { type: Function as PropType<EditorHostProps['openOutput']>, default: undefined },
  },
  setup(props) {
    const view = computed(() => viewStateOf(props.area.id))
    const pages = computed(() => pagesOf(props.doc))
    const activePage = computed(() => activePageOf(pages.value, props.doc.selection))
    const selectedSet = computed(() => new Set(props.doc.selection))

    /* ── 视口测量（`fit` 缩放要知道可视区多大）───────────────── */
    const scroller = ref<HTMLElement | null>(null)
    const viewSize = ref<{ w: number; h: number } | null>(null)
    let observer: ResizeObserver | null = null

    function measure(): void {
      const el = scroller.value
      if (!el) return
      const rect = el.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) viewSize.value = { w: rect.width, h: rect.height }
    }

    /**
     * 生效的缩放。
     *
     * ⚠️ 量不到尺寸时回退 **1:1**（而不是最小缩放）：无头环境
     * （happy-dom / 首帧）里所有 rect 都是 0，按 `fit` 算会得到 0.1 ——
     * 那时**断言和用户看到的都不是"纸"**。1:1 至少是一个确定的、可解释的值。
     */
    const zoom = computed(() => {
      const state = view.value
      if (state.zoom !== 'fit') return state.zoom
      const size = viewSize.value
      const page = activePage.value
      if (!size || !page) return 1
      return fitZoom(page.widthMm, page.heightMm, size.w, size.h)
    })

    /* ── 拖动现场 ─────────────────────────────────────────────── */
    let drag: DragState | null = null
    /**
     * 「现在指针在做什么」——`off` / `move` / `resize`。
     *
     * 它既是**样式**的依据（拖动时要不要换光标），也是**验证**的依据：
     * 指针交互出问题时，"事件到底有没有落到这个组件上"是第一个要回答的问题，
     * 而答案必须能从 DOM 上读出来（只靠 `console.log` 的话，
     * 无头验证里什么都没有）。
     */
    const dragMode = ref<'off' | 'move' | 'resize'>('off')
    const guides = ref<SnapGuide[]>([])

    function snapOptions(): { gridMm: number; thresholdMm: number } {
      const state = view.value
      return {
        gridMm: state.snap ? state.gridMm : 0,
        thresholdMm: pxToMm(SNAP_PX, zoom.value),
      }
    }

    /** 写回一个元素的框（只写变了的字段 —— 每个字段都是一次历史/发布） */
    function writeBox(id: string, next: ElementBox, before: ElementBox): void {
      const set = props.doc.setValue
      if (next.xMm !== before.xMm) set(id, 'xMm', next.xMm)
      if (next.yMm !== before.yMm) set(id, 'yMm', next.yMm)
      if (next.wMm !== before.wMm) set(id, 'wMm', next.wMm)
      if (next.hMm !== before.hMm) set(id, 'hMm', next.hMm)
    }

    /** 指针位置 → 纸面 mm（用**这一张纸自己的** rect：滚动 / 缩放下都对） */
    function pointToMm(ev: PointerEvent, pageKey: string): { x: number; y: number } | null {
      /*
       * ⚠️ 找的是**纸的实例**（`data-npp-page-key` = `${节点id}#${槽位}`），
       * 不是节点 id：同一个页面接两个 `pages.N` 时，两张纸在 DOM 里长得一样，
       * 用节点 id 去查只会拿到第一张 —— 在第二张纸上拖动，坐标会按第一张纸算
       * （表现是"组件一跳一跳地跑到别处"）。
       */
      const root = scroller.value?.querySelector<HTMLElement>(`[data-npp-page-key="${pageKey}"]`)
      if (!root) return null
      const rect = root.getBoundingClientRect()
      const scale = pxPerMm(zoom.value)
      return { x: (ev.clientX - rect.left) / scale, y: (ev.clientY - rect.top) / scale }
    }

    /**
     * 条码的缩放：写回的是**参数**（X 维度 µm / 条高 mm），不是宽高。
     *
     * ⚠️ 条码没有"宽度"参数 —— 宽度 = 模块数 × 整数点模块宽。
     * 所以"拖宽一点"只能翻译成"模块宽多一点"，见 `pageModel.resizeBarcode`。
     * 布局（网格 / 参考线吸附）对它不适用（模块宽本来就是量子化的），
     * 所以这条分支**不吸附**，只夹在纸内。
     */
    function applyBarcodeResize(el: ElementView, page: PageView, dxMm: number, dyMm: number): void {
      const view = el.barcode
      if (!view) return
      /*
       * ⚠️ 拖的是**框**（宽高），不是 X 维度：模块宽由布局按目标宽度取整算出来。
       * 这样"拖 → 宽变了"是直接的因果关系，也不会有"一个模块一跳"的颗粒感。
       */
      const params = {
        xMm: el.box.xMm,
        yMm: el.box.yMm,
        widthMm: el.box.wMm,
        heightMm: el.box.hMm,
      }
      const next = resizeBarcode(
        params,
        drag!.handle!,
        dxMm,
        dyMm,
        {
          moduleDots: view.moduleDots,
          moduleMm: view.moduleMm,
          /* ⚠️ 占地**总**模块数（含静区）—— 框宽就是它 × 模块宽 */
          modules: view.encoded?.modules ?? 1,
          square: view.square,
        },
        page,
        page.dpi,
      )
      const set = props.doc.setValue
      if (next.xMm !== params.xMm) set(el.id, 'xMm', next.xMm)
      if (next.yMm !== params.yMm) set(el.id, 'yMm', next.yMm)
      if (next.widthMm !== params.widthMm) set(el.id, 'wMm', next.widthMm)
      /*
       * ⚠️ 高度只写一次（二维码是正方形：宽高同一个数，写两遍会互相覆盖成
       * "先宽后高"的随机顺序）。1D 的 `hMm` 是**总高**（含 HRI）✓
       */
      if (view.square) {
        if (next.widthMm !== params.widthMm) set(el.id, 'hMm', next.widthMm)
      } else if (next.heightMm !== params.heightMm) {
        set(el.id, 'hMm', next.heightMm)
      }
    }

    function onWindowMove(ev: PointerEvent): void {
      if (!drag) return
      const page = drag.page
      const at = pointToMm(ev, page.key)
      if (!at) return
      const worldDx = at.x - drag.fromPx.x
      const worldDy = at.y - drag.fromPx.y
      /*
       * ⚠️ **位移要换算进组件自己的坐标系**：转了 90° 的组件，屏幕上
       * "往右拖"在它的局部是"往下拖"。不换算的话，用户拖旋转后的框
       * 会看到它**沿着屏幕轴走**（框和指针脱节），而缩放的把手更是完全对不上。
       */
      const { dxMm, dyMm } = toLocalDelta(worldDx, worldDy, drag.rotationDeg)
      const options = snapOptions()
      /*
       * ⚠️ 传 `drag.box`：等距候选要按**被拖框的尺寸**算
       * （"放这儿两边空隙一样大吗"只有知道框多宽才算得出来）。
       */
      const targets = snapTargetsFor(page, page.elements, drag.id, {
        wMm: drag.fromBox.wMm,
        hMm: drag.fromBox.hMm,
      })
      /*
       * ⚠️ **旋转过的组件不吸附**：吸附的语义是"边对齐纸上的网格 / 别的组件的边"，
       * 而转过 90° 之后"这个组件的右边"在纸上是竖着的 —— 拿它的 x 去和网格比
       * 只会得到看似随机的偏移。宁可不吸附（用户还在拖，位置能对上指针），
       * 也不要"吸到一个看不出来的地方"。
       */
      const snapOn = view.value.snap && drag.rotationDeg % 360 === 0

      /* 条码的**缩放**走自己的那条路（写参数），移动仍然按框走 */
      if (drag.mode === 'resize' && drag.barcode) {
        const current = page.elements.find((item) => item.id === drag!.id)
        if (current) applyBarcodeResize(current, page, dxMm, dyMm)
        return
      }

      if (drag.mode === 'move') {
        const moved = { ...drag.fromBox, xMm: drag.fromBox.xMm + dxMm, yMm: drag.fromBox.yMm + dyMm }
        const snapped = snapOn ? snapBox(moved, targets, options) : { box: moved, guides: [] as SnapGuide[] }
        guides.value = snapped.guides
        writeBox(drag.id, clampBoxToPage(snapped.box, page), drag.fromBox)
        return
      }

      const resized = resizeBox(drag.fromBox, drag.handle!, dxMm, dyMm, page)
      const snapped = snapOn
        ? snapResize(resized, drag.handle!, targets, options)
        : { box: resized, guides: [] as SnapGuide[] }
      guides.value = snapped.guides
      writeBox(drag.id, clampBoxToPage(snapped.box, page), drag.fromBox)
    }

    function endDrag(): void {
      if (!drag) return
      drag = null
      dragMode.value = 'off'
      guides.value = []
      props.doc.endEdit()
      if (typeof window !== 'undefined') {
        window.removeEventListener('pointermove', onWindowMove)
        window.removeEventListener('pointerup', endDrag)
        window.removeEventListener('pointercancel', endDrag)
      }
    }

    function beginDrag(ev: PointerEvent, state: Omit<DragState, 'fromPx'>): void {
      const at = pointToMm(ev, state.page.key)
      if (!at) return
      ev.preventDefault()
      drag = { ...state, fromPx: at }
      dragMode.value = state.mode
      props.doc.beginEdit(state.mode === 'move' ? '移动组件' : '调整组件尺寸')
      if (typeof window !== 'undefined') {
        window.addEventListener('pointermove', onWindowMove)
        window.addEventListener('pointerup', endDrag)
        window.addEventListener('pointercancel', endDrag)
      }
    }

    /* ── 元素上的指针 ─────────────────────────────────────────── */
    function onElementDown(ev: PointerEvent, el: ElementView, page: PageView): void {
      /*
       * ⚠️ 落在**把手**上时这里什么都不做（把手有自己的 `pointerdown`）。
       *
       * ⚠️ 这是**防御性**的一道，不是修出来的 bug：把手那边已经
       * `stopPropagation()` 了，而实测（happy-dom + 真机）它确实拦得住 ——
       * 把这一行删掉，端到端断言**照样绿**（我验过：删了不变红）。
       * 留着它的理由是"事件冒泡的语义在各实现里不完全一致"，
       * 而误判的代价（拖把手变成拖整个元素）很难查。别把它当成有测试守着的代码。
       */
      const target = ev.target as (HTMLElement & { hasAttribute?(name: string): boolean }) | null
      if (target?.hasAttribute?.('data-npp-handle')) return
      if (!selectedSet.value.has(el.id)) props.doc.select([el.id])
      beginDrag(ev, {
        mode: 'move',
        id: el.id,
        barcode: el.kind === 'barcode',
        handle: null,
        fromBox: el.box,
        rotationDeg: el.rotationDeg,
        page,
      })
    }

    function onHandleDown(ev: PointerEvent, el: ElementView, page: PageView, handle: ResizeHandle): void {
      ev.stopPropagation()
      beginDrag(ev, {
        mode: 'resize',
        id: el.id,
        barcode: el.kind === 'barcode',
        handle,
        fromBox: el.box,
        rotationDeg: el.rotationDeg,
        page,
      })
    }

    /** 点纸面空白 → 清空选中（和节点图里点空白是同一条语义） */
    function onPageDown(ev: PointerEvent, page: PageView): void {
      if (ev.target !== ev.currentTarget) return
      props.doc.select([])
    }

    /* ── 命令 ─────────────────────────────────────────────────── */
    function addComponent(kind: 'text' | 'box' | 'barcode' | 'qrcode' | 'image'): void {
      const page = activePage.value
      if (!page) return
      const type =
        kind === 'text'
          ? TEXT_TYPE
          : kind === 'box'
            ? BOX_TYPE
            : kind === 'image'
              ? IMAGE_TYPE
              : kind === 'qrcode'
                ? QRCODE_TYPE
                : BARCODE_TYPE
      const isCode = kind === 'barcode' || kind === 'qrcode'
      const wMm = kind === 'text' ? 40 : kind === 'box' ? 30 : kind === 'image' ? 30 : 0
      const hMm = kind === 'text' ? 10 : kind === 'box' ? 20 : kind === 'image' ? 20 : 12
      /*
       * ⚠️ 条码**不给初值**（除了高度）：它的宽度由 X 维度算出来，
       * 摆位也由它自己的宽度决定（居中要等编码完才知道多宽）。
       * 所以先建出来、再按**算出来的**宽度摆到纸中央（下面那段）。
       */
      /*
       * ★ **新节点落在现有节点附近**（真机反馈："点击添加组件节点之后，在节点图
       * 里边添加的节点往往距离现有节点很远，需要添加后再拖动到一起"）。
       *
       * ⚠️ 不给落点时宿主用**视口中心** —— 而图可能被拖到很远的地方，
       * 于是新组件孤零零地出现在空白处。这里改成：**从当前页节点往右下一格**，
       * 每多一个组件再错开一点（同一页的组件在图上是"一簇"）。
       */
      const anchor = props.doc.nodes.find((node) => node.id === page.id)
      const siblings = props.doc.nodes.filter((node) =>
        props.doc.links.some(
          (link) => link.source === node.id && link.target === page.id,
        ),
      ).length
      const at = anchor
        ? { x: anchor.position.x + 320, y: anchor.position.y + 40 + siblings * 120 }
        : undefined
      const id = props.doc.addNode(
        type,
        at,
        isCode
          ? kind === 'barcode'
            ? { xMm: 10, yMm: Math.max(0, Math.round((page.heightMm - hMm) / 2)), heightMm: hMm }
            : { xMm: 10, yMm: 10 }
          : { xMm: Math.max(0, Math.round((page.widthMm - wMm) / 2)), yMm: Math.max(0, Math.round((page.heightMm - hMm) / 2)), wMm, hMm },
      )
      if (!id) return
      /*
       * ⚠️ 建完**顺手接到当前页**：不接的话新组件根本不在纸面上
       * （成员关系读边），用户看到的是"点了一下什么都没发生"。
       * 接不上（类型/结构校验拒绝）时它落到「未接入」那一栏里，仍可手动接。
       */
      props.doc.connect(id, 'out', page.id, nextItemsHandle(props.doc.links, page.id))
      /* 条码 / 二维码：接上之后才知道自己多大 —— 现在把它摆到纸中央 */
      if (isCode) {
        const element = pages.value
          .find((item) => item.id === page.id)
          ?.elements.find((item) => item.id === id)
        if (element) {
          props.doc.setValue(id, 'xMm', Math.max(0, Math.round((page.widthMm - element.box.wMm) / 2)))
          props.doc.setValue(id, 'yMm', Math.max(0, Math.round((page.heightMm - element.box.hMm) / 2)))
        }
      }
    }

    /**
     * **新建页面**：没有文档就先建文档，再把这一页接上去。
     *
     * ⚠️ 真机反馈："点击添加页面之后，它只添加了页面，没有添加文档节点，
     * 正常应该同时添加文档节点和一个页面节点"。
     *
     * 理由不止是"少点一步"：**纸张参数归文档**，所以只有页面节点的话，
     * 那一页的宽度 / 高度 / 分辨率 / 背景色**全都改不了**（面板里是只读的）。
     * 空模板上点「新建页面」是用户的第一个动作 —— 那一步必须把他带到
     * 一个**能改东西**的状态。
     *
     * ⚠️ 顺序：先建文档（后面 `addComponent` 之类的动作依赖"当前文档"），
     * 再接页，最后**把文档的纸张抄给谁都不用** —— 新文档自带默认纸张，
     * 而页面上的旧值只是兜底读取。
     */
    function createPage(): void {
      const docId = documentNodeOf(props.doc)?.id ?? props.doc.addNode(DOCUMENT_TYPE)
      const pageId = props.doc.addNode(PAGE_TYPE)
      if (docId && pageId) {
        /*
         * ⚠️ 接在**下一个空槽**上（不是"页数"那一位）：页码不压实（见
         * `orderedPages`），断开中间一路之后按页数算会把新页接到**已占**的槽位上，
         * 而宿主的覆盖语义会把原来那一页顶掉（表现是"加了一页，另一页没了"）。
         */
        props.doc.connect(pageId, 'out', docId, nextPagesHandle(props.doc.links, docId))
        props.doc.select([pageId])
      }
    }

    /**
     * 导出 IR（`node-print.rendertree`）—— **交给 `design_lib` 的那份文件**。
     *
     * ⚠️ 下载那几行在无头环境里没有 `document` / `URL`，所以整段是**受保护的**：
     * 纯数据那一半（`compileToFile`）才是被断言的部分，下载只是把它落到磁盘。
     */
    /**
     * **这一份活的作业参数**（从文档节点的值里读）。
     *
     * ⚠️ 走 `normalizeJob`：值可能来自手改的文档（份数写成 0 / 浓度 99），
     * 而 IR 是**给执行侧看的契约** —— 越界值在那边会变成"机器不动"。
     */
    function jobOf(): PrintJob {
      const doc = documentNodeOf(props.doc)
      return normalizeJob({
        copies: Number(doc?.values.copies),
        cut: doc?.values.cut as PrintJob['cut'],
        darkness: Number(doc?.values.darkness),
        speed: doc?.values.speed as PrintJob['speed'],
        media: doc?.values.media as PrintJob['media'],
        feedMm: Number(doc?.values.feedMm),
      })
    }

    function exportIr(): void {
      const file = compileToFile(pages.value, jobOf())
      if (typeof document === 'undefined' || typeof URL === 'undefined') return
      const blob = new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'print-rendertree.json'
      anchor.click()
      URL.revokeObjectURL(url)
    }

    function stepZoom(factor: number): void {
      const state = view.value
      state.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom.value * factor))
    }

    onMounted(() => {
      ensurePrintStyles()
      measure()
      if (typeof ResizeObserver !== 'undefined' && scroller.value) {
        observer = new ResizeObserver(() => measure())
        observer.observe(scroller.value)
      }
    })
    onBeforeUnmount(() => {
      observer?.disconnect()
      observer = null
      endDrag()
    })

    /* ── 渲染 ─────────────────────────────────────────────────── */
    /**
     * 标尺刻度（横向 / 纵向共用）。
     *
     * ⚠️ 步长是**缩放的函数**（`rulerStepMm`）：固定步长在放大时浪费、
     * 缩小时挤成一团。刻度位置按 mm 摆，所以它和纸上的东西永远对得上。
     */
    function renderTicks(lengthMm: number, zoomValue: number, axis: 'x' | 'y'): VNode[] {
      return rulerTicks(lengthMm, rulerStepMm(zoomValue)).map((mm) =>
        h(
          'span',
          {
            class: cls('tick'),
            'data-mm': mm,
            style: axis === 'x' ? { left: `${mmToPx(mm, zoomValue)}px` } : { top: `${mmToPx(mm, zoomValue)}px` },
          },
          String(mm),
        ),
      )
    }

    /**
     * **条码 / 二维码的画法**：一个 `<svg>` + 一条 `<path>`。
     *
     * 为什么用 SVG 而不是一堆 div：2D 符号是 21×21 起步的模块矩阵，
     * 逐模块建 DOM 会有几百个节点；而每条扫描线上的连续模块可以并成一条
     * 子路径（一个矩形），一个 `<path>` 就画完了。1D 的条也是同样的做法。
     *
     * ⚠️ 几何一律走**整数点**：`moduleDots × 模块坐标`，再乘 `zoom` 只影响
     * 屏幕像素，不影响"条宽是不是整数个点"这件事 —— 后者是扫码率的前提。
     *
     * ⚠️ `d` 由 `pathData.ts` 拼（编辑视图与精确预览**同一处**）：
     * 这里原来自己拼了一份，而预览那份把子路径数组直接塞进 `d` 了 ——
     * 两份实现分叉的代价见那个文件的文件头（浏览器报 "Problem parsing d"）。
     */
    function renderBarcode(view: BarcodeView, box: ElementBox, zoomValue: number): VNode {
      const pxPerModule = mmToPx(view.moduleMm, zoomValue)
      const barHeightPx = mmToPx(view.barHeightMm, zoomValue)
      const quietPx = mmToPx(view.quietZoneMm, zoomValue)
      const totalWidthPx = mmToPx(box.wMm, zoomValue)
      const totalHeightPx = mmToPx(box.hMm, zoomValue)
      /*
       * ★★ **内容要躲开框**（和 IR 的 `insetDots` 是同一件事、同一个来源）：
       * 框画在组件框上，静区必须在框**里面** —— 不内缩的话，描边内侧的
       * 半个线宽会压在静区里（扫码率的前提，`verify:raster` 那条断言盯着它）。
       * ⚠️ 用 `view.insetMm`（`barcodeElementOf` 算好的）而不是在这儿自己算一遍：
       * 各算一份就是"编辑视图和预览差一个点"那类分叉。
       */
      const insetPx = mmToPx(view.insetMm, zoomValue)
      let d = ''

      if (view.encoded?.kind === '1d') {
        d = barsPathData({
          bars: view.encoded.bars,
          /* 静区在这里加进来（`bars` 是内容区坐标）—— 再外面是框让出来的那一圈 */
          originX: insetPx + quietPx,
          originY: insetPx,
          moduleSize: pxPerModule,
          height: barHeightPx,
        })
      } else if (view.encoded?.kind === '2d') {
        /* 每一行把**连续的模块**并成一个矩形（`matrixRuns` 一次扫描就生成） */
        const { cols, rows, matrix } = view.encoded
        d = matrixPathData({
          rows,
          cols,
          ink: (row, col) => matrix[row * cols + col] === true,
          /* 二维四周都有静区：矩阵原点 = 静区之后（x / y 都偏移一个静区） */
          originX: insetPx + quietPx,
          originY: insetPx + quietPx,
          moduleSize: pxPerModule,
        })
      }

      const children: (VNode | null)[] = []
      if (view.error) {
        children.push(h('div', { class: cls('barcode-error'), 'data-npp-barcode-error': view.error }, view.error))
      } else {
        /*
         * 内容的**边界框**（虚线）：静区是规范要求，用户要看得见它占了多少地方 ——
         * 只画条的话，"为什么这个条码比我想的宽"永远说不清。
         */
        children.push(
          h('span', {
            class: cls('barcode-frame'),
            /* ⚠️ 这条虚线标的是**内容区**（静区的内边界）—— 同样要让开框那一圈 */
            style: {
              left: `${insetPx + quietPx}px`,
              width: `${totalWidthPx - insetPx * 2 - quietPx * 2}px`,
              height: `${barHeightPx}px`,
            },
          }),
        )
        children.push(
          h(
            'svg',
            {
              class: cls('barcode-svg'),
              width: `${totalWidthPx}px`,
              height: `${totalHeightPx}px`,
              viewBox: `0 0 ${totalWidthPx} ${totalHeightPx}`,
            },
            /* ⚠️ `d` 已经是**字符串**（`pathDataOf` 拼好的）—— 别在这儿再包一层数组 */
            [h('path', { d, fill: 'currentColor' })],
          ),
        )
      }
      if (view.showHri && !view.error) {
        children.push(
          h(
            'div',
            {
              class: cls('barcode-hri'),
              /* ⚠️ HRI 在条下面 —— 条的顶边也已经让开了框那一圈（`insetPx`） */
              style: { top: `${insetPx + barHeightPx}px`, height: `${mmToPx(view.hriMm, zoomValue)}px`, fontSize: `${mmToPx(view.hriMm, zoomValue) * 0.8}px` },
            },
            view.text,
          ),
        )
      }
      return h('span', { class: cls('barcode') }, children)
    }

    /**
     * **组件框 / 底那一层** —— 矩形、文本、条码、二维码、图片**共用这一个来源**。
     *
     * ⚠️ 抽出来不是为了少写几行，而是为了让"编辑视图画不画框"这件事**只有一个答案**：
     * 原来这一层只有矩形（`__box`）和文本（`__text-frame`）画，条码 / 二维码 /
     * 图片**一个都不画** —— 而 IR 那边 `pushFrameOp()` 是**所有非矩形组件**都发一条
     * frame op。两头的判据不同，于是：
     *
     * ```text
     *   条码 / 二维码：IR 那条 op 依赖 ElementView.lineWidthMm，而那个值原来写死 0
     *                  → 编辑器和预览**都没有框**（参数改了没反应）
     *   图片：        lineWidthMm 有值（参数表默认 0.3mm）→ 预览有框、编辑视图没有
     * ```
     *
     * 两个方向都是"编辑视图 ≠ 预览"，成因却不同 —— 所以判据落在**同一个函数**上：
     * `el.lineWidthMm / el.fill / el.invert`，也就是 `pushFrameOp()` 读的那三个字段。
     *
     * ⚠️ 这一层画在内容**之下**（CSS 的 `z-index: -1` + `.npp__ink` 自成层叠上下文）：
     * IR 的指令顺序就是"框先画、内容后画"，编辑视图的顺序必须一样 ——
     * 否则"填充"会把文字 / 图片盖住（而那在预览里盖不住）。
     */
    function renderFrameLayer(el: ElementView, zoomValue: number, className: string): VNode | null {
      if (!(el.lineWidthMm > 0 || el.fill || el.invert)) return null
      return h('span', {
        class: cls(className),
        'data-npp-frame': el.id,
        style: {
          /* ⚠️ `Math.max(1, …)`：0.1mm 的线在屏幕上不到半个像素，画成 0 就是"设了看不见" */
          border:
            el.lineWidthMm > 0
              ? `${Math.max(1, mmToPx(el.lineWidthMm, zoomValue))}px solid ${el.strokeColor}`
              : 'none',
          /* 反白 = 黑底（热敏打不出白墨：先铺底再掏空内容）；否则填充开着才铺背景色 */
          background: el.invert ? el.strokeColor : el.fill ? el.fillColor : 'none',
          borderRadius: el.radiusMm > 0 ? `${mmToPx(el.radiusMm, zoomValue)}px` : '0',
        },
      })
    }

    function renderElement(el: ElementView, page: PageView, zoomValue: number): VNode {
      const selected = selectedSet.value.has(el.id)
      const box = el.box
      const children: (VNode | null)[] = []

      /*
       * 反白 = **先铺一块实心黑，再写字**（热敏打印机打不出白墨）。
       * 它不是"把字改成白色"那么简单：字号 / 位置 / 行距都不变，
       * 只是多了一层底 —— 所以底必须**正好是组件框**，多一个点都会印到纸上。
       */
      if (el.invert) {
        children.push(h('span', { class: cls('invert'), style: { background: 'currentColor' } }))
      }

      if (el.kind === 'image' && el.image) {
        /*
         * ⚠️ 图片**也有框与底**（它从矩形扩展，参数表里那几行是同一批）：
         * IR 一直在发 frame op，编辑视图不画就是"预览里有框、编辑里没有"。
         */
        children.push(renderFrameLayer(el, zoomValue, 'box'))
        /*
         * ⚠️ 没源时画**占位框 + 一句话**（而不是空白）：空白看起来像渲染坏了，
         * 而"还没有图像源"是一句可执行的提示。
         */
        children.push(
          el.image.hasSource
            ? h('img', {
                class: cls('image'),
                'data-npp-image': 'src',
                src: el.image.src,
                alt: el.title,
                draggable: false,
                /*
                 * ⚠️ **编辑视图里也做二值化**（`filter: url(#阈值滤镜)`）。
                 *
                 * 真机反馈："上传了一张主色是黄色的图片，绘制出来也是黄色的，
                 * 不是黑白的" —— 热敏只有"上墨 / 不上墨"，一张彩色照片在纸上
                 * 只会是一片黑或一片白。看原图就**没法判断"这张图印得出来吗"**，
                 * 也看不到调 `threshold` 的效果。
                 *
                 * ⚠️ 和参数颜色（矩形 / 文字的色块）不同：那些在编辑视图里保留
                 * 真色 —— 因为那时看的是"我选了什么颜色"（意图）；而图像的颜色
                 * 不在参数里，只能在纸上判断（结果）。
                 */
                style: {
                  objectFit: el.image.fit === 'fill' ? 'fill' : el.image.fit,
                  filter: `url(#${thresholdFilterId(el.image.threshold)})`,
                },
                'data-npp-threshold': String(el.image.threshold),
              })
            : h(
                'span',
                { class: cls('image-empty'), 'data-npp-image': 'empty' },
                '还没有图像源（在「内容」里填地址或 data: URI）',
              ),
        )
      } else if (el.kind === 'barcode' && el.barcode) {
        /*
         * ⚠️ 条码 / 二维码的框 / 底**也走这一层**（真机反馈："预览模式的组件边框
         * 是默认值，不是设置值，然后就编辑模式显示粗，预览就细了"）。
         *
         * 参数表里条码继承自矩形那条链，所以「线宽 / 边框颜色 / 背景颜色 / 填充 /
         * 圆角」五行**一直都在节点上** —— 只有这里和 `barcodeElementOf` 一起
         * 把它们接上，"改了没反应的控件"才消失。
         */
        children.push(renderFrameLayer(el, zoomValue, 'box'))
        children.push(renderBarcode(el.barcode, box, zoomValue))
      } else if (el.kind === 'text') {
        /*
         * ⚠️ 文本是**从矩形扩展**来的：所以它也画边框与背景（`strokeColor` /
         * `fillColor` / 圆角 / 反白）—— 第一版只画字，于是文本上的边框参数
         * 成了"改了没反应"的控件。
         */
        children.push(renderFrameLayer(el, zoomValue, 'text-frame'))
        /*
         * 行距：0 = 自动（字号的 1.15 倍）。**必须和 IR 用同一个函数**
         * （`lineHeightOf`）—— 编辑器里排 3 行、打印出来挤成 2 行半，
         * 就是这里各算了一次。
         */
        const linePx = mmToPx(lineHeightOf(el), zoomValue)
        children.push(
          h(
            'span',
            {
              class: cls('text'),
              style: {
                fontSize: `${mmToPx(el.sizeMm, zoomValue)}px`,
                lineHeight: `${linePx}px`,
                fontWeight: el.bold ? '700' : '400',
                width: '100%',
                /*
                 * 垂直对齐靠 flex：**不能**用 `align-items` 之外的偏移去凑 ——
                 * 偏移量要按"文字实际高度"算，而那个高度只有渲染器知道
                 * （IR 那边是把这条规矩交给执行侧的，见 `type-contract` §8.1）。
                 */
                display: 'flex',
                flexDirection: 'column',
                justifyContent:
                  el.vAlign === 'middle' ? 'center' : el.vAlign === 'bottom' ? 'flex-end' : 'flex-start',
                textAlign: el.align,
                /* ⚠️ 字色来自 `color`（文字颜色），不是边框色 */
                color: el.invert ? el.fillColor : el.color,
                position: 'relative',
              },
            },
            el.text,
          ),
        )
      } else {
        /* ⚠️ 矩形：**它自己那条 op 就是框**（IR 里 `kind === 'box'` 不发 frame op） */
        children.push(renderFrameLayer(el, zoomValue, 'box'))
      }

      const handleNodes: VNode[] = []
      if (selected) {
        for (const handle of RESIZE_HANDLES) {
          const west = handle.includes('w')
          const east = handle.includes('e')
          const north = handle.includes('n')
          const south = handle.includes('s')
          handleNodes.push(
            h('span', {
              class: cls('handle'),
              'data-npp-handle': handle,
              style: {
                left: west ? '0' : east ? '100%' : '50%',
                top: north ? '0' : south ? '100%' : '50%',
                cursor: `${handle}-resize`,
              },
              onPointerdown: (ev: PointerEvent) => onHandleDown(ev, el, page, handle),
            }),
          )
        }
      }

      /*
       * ⚠️ 画出来的东西统一包在 `ink` 里：**旋转只作用在这一层**
       * （`transform-origin: center` = 绕框中心，和 IR 的 `pivotDots` 同一条约定）。
       * 它不参与布局（`absolute; inset: 0`），所以不会把框撑变形。
       */
      const ink = h(
        'span',
        {
          class: cls('ink'),
          'data-npp-ink': el.id,
          'data-opacity': String(el.opacity),
          style: {
            /* 不透明度：0–1（1-bit 输出要靠抖动近似，见 IR 那边的说明） */
            ...(el.opacity < 1 ? { opacity: String(el.opacity) } : {}),
            ...(el.rotationDeg !== 0 ? { transform: `rotate(${el.rotationDeg}deg)` } : {}),
          },
        },
        children,
      )

      /*
       * ★ **圆角真的在裁**（真机反馈："其他组件有圆角的，也需要有这种裁剪效果"）。
       *
       * ⚠️ 裁剪必须单独一层（`clip`），**不能**加在外层 `.el` 上：
       * 那层里还有选中把手，而把手是骑在框边上的 —— 一起裁掉，圆角一开
       * 把手就少一半，缩放就没法用了。
       *
       * ⚠️ 这一层和 IR 的 `clip`（`PrintClip`）是同一条语义：
       * 编辑器裁什么，执行侧就裁什么。
       */
      const clipped =
        el.radiusMm > 0
          ? h(
              'span',
              {
                class: cls('clip'),
                'data-npp-clip': `${Math.round(el.radiusMm * 100) / 100}`,
                style: { borderRadius: `${mmToPx(el.radiusMm, zoomValue)}px` },
              },
              [ink],
            )
          : ink

      return h(
        'div',
        {
          class: cls('el'),
          'data-npp-el': el.id,
          'data-node-type': el.nodeType,
          'data-kind': el.kind,
          'data-selected': String(selected),
          /* ⚠️ mm 摆在 4 位小数的 data 上：验证与排错读它，不去反解 px（那要乘缩放） */
          'data-mm': `${box.xMm},${box.yMm},${box.wMm},${box.hMm}`,
          'data-rotation': String(el.rotationDeg),
          /*
           * 条码那几个**算出来的**数字也摆出来：验证要断言"模块宽是整数点"
           * "编码失败时是占位框"，而它们不是参数、读不到别处去。
           */
          ...(el.barcode
            ? {
                'data-symbology': el.barcode.symbology,
                'data-module-dots': String(el.barcode.moduleDots),
                'data-encode-error': el.barcode.error ?? '',
                ...(el.barcode.encoded?.kind === '1d'
                  ? {
                      'data-content-modules': String(el.barcode.encoded.contentModules),
                      'data-bars': String(el.barcode.encoded.bars.length),
                    }
                  : {}),
                ...(el.barcode.encoded?.kind === '2d'
                  ? { 'data-matrix': `${el.barcode.encoded.cols}x${el.barcode.encoded.rows}` }
                  : {}),
              }
            : {}),
          title: el.title,
          /*
           * ⚠️ **旋转不在这层**（真机反馈："旋转应该是旋转绘制效果，
           * 而不是旋转组件的坐标系"）。
           *
           * 这一层是**框**：位置 / 尺寸 / 选中 / 把手全按正的坐标走
           * （所以拖动、吸附、命中判定都不受旋转影响 —— 那才是用户的手感）。
           * 旋转写在里层的 `ink` 上，只有画出来的内容转。
           */
          style: {
            left: `${mmToPx(box.xMm, zoomValue)}px`,
            top: `${mmToPx(box.yMm, zoomValue)}px`,
            width: `${mmToPx(box.wMm, zoomValue)}px`,
            height: `${mmToPx(box.hMm, zoomValue)}px`,
            /*
             * ⚠️ `color` 是**墨色**（前景色）：文字的字、条码的条、二维码的
             * 模块都用它（`currentColor` 传下去）。**不是**边框色 ——
             * 边框色是 `strokeColor`，那一条只画组件的框。
             */
            color: el.color,
          },
          onPointerdown: (ev: PointerEvent) => onElementDown(ev, el, page),
        },
        /*
         * ⚠️ 画出来的内容包在 `ink` 里（旋转只作用它）、需要时再包一层 `clip`
         * （圆角裁剪）；**把手在最外面**（不跟着转、也不被裁）。
         */
        [clipped, ...(selected ? handleNodes : [])],
      )
    }

    /**
     * 编辑视图里用到的**二值化滤镜定义**（按阈值去重）。
     *
     * 没有图像 / 不需要二值化时就**不渲染**这个 `<svg>`（空元素也是开销，
     * 而且验证脚本要靠"在不在"判断这件事）。
     */
    function renderThresholdDefs(pages: readonly PageView[]): VNode | null {
      const levels = new Set<number>()
      for (const page of pages) {
        for (const element of page.elements) {
          if (element.kind === 'image' && element.image?.hasSource) {
            levels.add(element.image.threshold)
          }
        }
      }
      if (levels.size === 0) return null
      return h(
        'svg',
        { class: cls('defs'), 'data-npp-defs': 'threshold', 'aria-hidden': 'true' },
        [...levels].map((level) => thresholdFilterNode(thresholdFilterId(level), level)),
      )
    }

    /**
     * 一张纸 + 它的标尺与标签。
     *
     * ⚠️ **纸是绝对定位的，所以外面那层壳必须自己给出尺寸**（下面那两行
     * `width` / `height` 不是装饰）：不给的话壳的高度只剩标签那一行，
     * 而纸**溢出**到壳外 —— 表现是多张纸叠在一起、标签压在纸上。
     * 这是纯 CSS 看不出来的那类错（DOM 结构完全正常）。
     *
     * 数字来自 `styles.ts` 里那几条约定：纸从 `left: 14px` / `top: 12px` 开始
     * （左标尺占 14px、上标尺占 12px），右侧再留 14px，标签一行约 26px。
     */
    /**
     * 这个纸色算不算"浅色"（用于决定网格线画深还是画浅）。
     *
     * ⚠️ 用**亮度阈值**而不是"是不是白"：浅黄、浅灰的纸同样需要深网格。
     * 认 `#rgb` / `#rrggbb` / `rgb(...)` 三种写法（内联样式与 computed 各给一种）。
     */
    function isLightBackground(color: string): boolean {
      const hex = color.trim().toLowerCase()
      let r = 255
      let g = 255
      let b = 255
      if (hex.startsWith('#')) {
        const full = hex.length === 4 ? [...hex.slice(1)].map((c) => c + c).join('') : hex.slice(1, 7)
        r = Number.parseInt(full.slice(0, 2), 16)
        g = Number.parseInt(full.slice(2, 4), 16)
        b = Number.parseInt(full.slice(4, 6), 16)
      } else {
        const rgb = hex.match(/rgba?\(([^)]+)\)/)
        if (rgb) {
          const [rr, gg, bb] = rgb[1]!.split(',').map((part) => Number(part.trim()))
          r = rr ?? 255
          g = gg ?? 255
          b = bb ?? 255
        }
      }
      /* 感知亮度（人眼对绿最敏感）—— 0.6 以上算浅色纸 */
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6
    }

    /** 二级行（可横滚的那条） */
    const barEl = ref<HTMLElement | null>(null)

    /** 二级行的横滚（滚轮；触摸横滑是 CSS 的 `touch-action: pan-x`） */
    const stripScroll = createStripScroll({ el: () => barEl.value })

    /** 是不是白（`#fff` / `#ffffff` / 大小写都算）—— 白 = 不铺底，也就不必提示 */
    function isWhite(color: string): boolean {
      const hex = color.trim().toLowerCase()
      if (hex === '#fff' || hex === '#ffffff') return true
      const rgb = hex.match(/rgba?\(([^)]+)\)/)
      if (!rgb) return false
      const [r, g, b] = rgb[1]!.split(',').map((part) => Number(part.trim()))
      return r === 255 && g === 255 && b === 255
    }

    const RULER_LEFT = 14
    const RULER_TOP = 12
    const SHEET_PAD_RIGHT = 14
    const SHEET_LABEL_H = 26

    /** 纸下面那行字（尺寸 / 分辨率 / 名字 / 纸色提示）—— 两个视图共用 */
    function sheetLabelOf(page: PageView): string {
      return (
        `${page.widthMm}×${page.heightMm}mm · ${page.dpi}dpi · ${page.title}` +
        /*
         * ⚠️ 非白纸色时把"仅预览"写在纸上：它是**介质模拟**，
         * 不是打印指令（打印机不知道纸什么颜色）。
         */
        (isWhite(page.backgroundColor) ? '' : ` · 纸色 ${page.backgroundColor}（仅预览）`)
      )
    }

    /**
     * ★★ **一张纸的外壳**：标尺 + 内容 + 标签 —— **编辑视图与精确预览共用这一个**。
     *
     * ⚠️ 为什么必须共用（真机反馈：「编辑模式和预览模式切换的时候，为什么标尺会
     * 消失」）：标尺原来长在 `renderSheet()` 里，而那是**编辑视图**的画法 ——
     * 预览那份 `renderPreview()` 直接画 IR，纸外面什么都没有。
     * 标尺是**"这张纸多大"的度量**，两个视图都该有（预览里更该有：那时候在核对
     * 版面）。而且刻度值只能算**一次**：两处各写一段 `rulerTicks(rulerStepMm(...))`
     * 就是"同一张纸在两个视图里尺子不一样"那类分叉。
     *
     * ⚠️ 外壳必须自报尺寸（下面那两行 `width` / `height` 不是装饰）：纸是
     * `position: absolute` 的，不给尺寸的话壳只剩标签那一行高，纸**溢出**到壳外 ——
     * 表现是多张纸叠在一起（DOM 结构完全正常，纯 CSS 看不出来）。
     */
    function renderSheetChrome(
      page: PageView,
      zoomValue: number,
      contentWidthPx: number,
      contentHeightPx: number,
      content: (VNode | null)[],
    ): VNode {
      const state = view.value
      return h(
        'div',
        {
          class: cls('sheet'),
          'data-npp-sheet': page.id,
          /* ⚠️ 实例标识：同一个页面印两次时，两张纸靠它分得开（见 `PageView.key`） */
          'data-npp-sheet-key': page.key,
          'data-sheet-px': `${Math.round(contentWidthPx)}x${Math.round(contentHeightPx)}`,
          style: {
            width: `${contentWidthPx + RULER_LEFT + SHEET_PAD_RIGHT}px`,
            height: `${contentHeightPx + RULER_TOP + SHEET_LABEL_H}px`,
          },
        },
        [
          /* ⚠️ 刻度按 **mm** 摆：它和纸上的东西永远对得上（两个视图同一个 zoom） */
          state.rulers
            ? h('div', { class: cls('ruler-x'), 'data-npp-ruler': 'x' }, renderTicks(page.widthMm, zoomValue, 'x'))
            : null,
          state.rulers
            ? h('div', { class: cls('ruler-y'), 'data-npp-ruler': 'y' }, renderTicks(page.heightMm, zoomValue, 'y'))
            : null,
          ...content,
          h(
            'div',
            {
              class: cls('label'),
              'data-npp-sheet-label': page.id,
              'data-npp-sheet-label-key': page.key,
              /*
               * ⚠️ **必须显式定位**：纸是 `position: absolute`（它要压在标尺
               * 交角上），所以纸**不在流里** —— 标签留在流里的话会被画在
               * 纸的下面，然后被纸整块盖住。
               *
               * 真机反馈："页面编辑器的页面背景后面，我好像看到了一些信息，
               * 但是被页面挡住了" —— 就是这行「1. 页面 · 60×30mm · 203dpi」
               * 从纸的边上露出了一条。
               */
              style: { top: `${RULER_TOP + contentHeightPx + 4}px` },
            },
            sheetLabelOf(page),
          ),
        ],
      )
    }

    /**
     * **纸上的覆盖层**：网格 + 边距线 —— **两个视图共用**（2027 改）。
     *
     * ## 为什么（用户实测报的）
     *
     * 用户原话：
     *
     * > 预览和编辑模式的显示效果不一样
     *
     * 差异里最扎眼的一条就是**网格**：编辑视图有、预览没有。而"网格 / 标尺"
     * 是**视图开关**（工具栏上那两个按钮，状态在 `viewStateOf`）——
     * 用户把它们打开，意思是"我要看网格"，而不是"我要在编辑视图里看网格"。
     * 标尺那次（"切预览标尺会消失"）已经定过这条规矩，网格是同一个道理。
     *
     * ⚠️ 边距线（`margin`）一起跟过来：它也是"这张纸的几何辅助"，
     * 和网格同一类（**不是**编辑手柄那种只在编辑时才有意义的东西）。
     * 判据：**它画的是纸的属性，不是可点的控件**。
     *
     * ⚠️ 网格线**很淡**（真机反馈："网格有点太明显了可以再淡点，大概是 20%
     * 或者 30% 的透明度的样子"）—— 它的作用是"看着别放歪"，不抢注意力。
     */
    function renderSheetOverlays(page: PageView, zoomValue: number): (VNode | null)[] {
      const state = view.value
      const gridPx = state.gridMm > 0 ? mmToPx(state.gridMm, zoomValue) : 0
      /* 纸是浅色 → 深网格；纸是深色 → 浅网格（不然网格会"消失"） */
      const gridColor = isLightBackground(page.backgroundColor)
        ? 'rgba(0,0,0,0.045)'
        : 'rgba(255,255,255,0.04)'
      const margin = mmToPx(page.marginMm, zoomValue)
      return [
        gridPx > 0
          ? h('div', {
              class: cls('grid'),
              'data-npp-grid': `${state.gridMm}`,
              style: {
                backgroundImage:
                  `repeating-linear-gradient(to right, ${gridColor} 0 1px, transparent 1px ${gridPx}px),` +
                  `repeating-linear-gradient(to bottom, ${gridColor} 0 1px, transparent 1px ${gridPx}px)`,
              },
            })
          : null,
        /* 边距（静区）：内容不该越过的边界 —— 打印机的物理不可打印区 */
        h('div', {
          class: cls('margin'),
          'data-npp-margin': `${page.marginMm}`,
          style: {
            left: `${margin}px`,
            top: `${margin}px`,
            right: `${margin}px`,
            bottom: `${margin}px`,
          },
        }),
      ]
    }

    function renderSheet(page: PageView, zoomValue: number): VNode {
      const state = view.value
      const width = mmToPx(page.widthMm, zoomValue)
      const height = mmToPx(page.heightMm, zoomValue)
      return renderSheetChrome(page, zoomValue, width, height, [
        h(
          'div',
          {
            class: cls('page'),
            'data-npp-page': page.id,
            /* ⚠️ 指针换算按**实例**找纸（同一个页面印两次时 id 分不开） */
            'data-npp-page-key': page.key,
            'data-page-mm': `${page.widthMm}x${page.heightMm}`,
            'data-dpi': page.dpi,
            /* ⚠️ 纸的底色**来自文档参数**（不是写死的白）：验证脚本读它 */
            'data-background': page.backgroundColor,
            style: { width: `${width}px`, height: `${height}px`, background: page.backgroundColor },
            onPointerdown: (ev: PointerEvent) => onPageDown(ev, page),
          },
          [
            /*
             * ⚠️ 网格线**跟着纸色走**（`isLightBackground`）：纸浅用深网格、
             * 纸深用浅网格。写死一种颜色的表现是"换了个纸色，网格就看不见了"。
             */
            ...renderSheetOverlays(page, zoomValue),
            ...page.elements.map((el) => renderElement(el, page, zoomValue)),
              ...guides.value.map((guide) =>
                h('div', {
                  class: [cls('guide'), cls(`guide--${guide.axis}`)],
                  'data-npp-guide': guide.axis,
                  style:
                    guide.axis === 'x'
                      ? { left: `${mmToPx(guide.atMm, zoomValue)}px` }
                      : { top: `${mmToPx(guide.atMm, zoomValue)}px` },
                }),
              ),
            ],
          ),
      ])
    }

    /**
     * **精确预览**：把 IR 画出来（纯黑白、几何按整数点吸附）。
     *
     * ⚠️ 画的是 **IR 的 op**，不是组件 —— 这一步的意义正在于此：
     * 用户看到的和 `design_lib` 拿到的**是同一份数据**，
     * 所以"预览里贴边、打印出来也贴边"不是巧合。
     *
     * ⚠️ 为什么用 SVG 而不是 Canvas2D：一是**可断言**（无头环境里 canvas
     * 拿不到上下文，那等于这块没被测过），二是矢量在这个尺度下更干净
     * （`shape-rendering: crispEdges` + 整数点吸附 = 该硬的地方硬）。
     * 权威光栅仍然由 Skia 那一侧出（架构文档 §4.2 的绘制层）。
     */
    function renderPreview(tree: PageRenderTree, zoomValue: number, page: PageView): VNode {
      const background = page.backgroundColor
      /*
       * ★★ **SVG 内部一律用设备点**（`viewBox` 就是点）。
       *
       * 2027 改：以前这里是 `toPx(dots)`（点 → mm → px），viewBox 也用同一套换算
       * —— 那让"纸的像素尺寸"由**取整后的点**倒推（60mm → 480 点 → 60.0591mm），
       * 于是预览的纸比编辑视图的纸宽 0.1%，两个视图的纸**不是同一张**。
       * 现在：外框用 mm（和编辑视图同一个数）、viewBox 用点、内容用点 ——
       * 那 0.1% 由 SVG 自己缩放掉，几何真源仍是 IR。
       */
      const DOT = (dots: number) => dots
      /*
       * 旋转：SVG 的 `rotate(角度, cx, cy)` —— 角度是**顺时针**，
       * 和文档层 / IR 的约定一致（`type-contract` §8.1）。
       */
      /** op 的通用属性：旋转 + 不透明度（1-bit 输出要靠抖动近似，这里如实画） */
      const commonAttrs = (op: {
        rotation?: { rotationDeg: number; pivotXDots: number; pivotYDots: number }
        opacity?: number
      }) => ({
        opacity: op.opacity !== undefined && op.opacity < 1 ? op.opacity : undefined,
      })
      const rotationAttr = (op: { rotation?: { rotationDeg: number; pivotXDots: number; pivotYDots: number } }) =>
        op.rotation
          ? `rotate(${op.rotation.rotationDeg} ${DOT(op.rotation.pivotXDots)} ${DOT(op.rotation.pivotYDots)})`
          : undefined
      /*
       * ══════════════════════════════════════════════════════════════════
       * ★ **二值化**：这一屏画的是"热敏机真会打出来的样子"。
       *
       * 真机反馈："热敏打印的话，颜色和图像可能需要做二值处理，不然放一张图
       * 就全是黑色的了"。
       *
       * 热敏只有"上墨 / 不上墨"：**白 = 纸，别的颜色 = 墨**。所以这里把颜色
       * 量化成"上墨与否"（非白 → 黑），把图片按 `threshold` 过一道 1-bit。
       *
       * ⚠️ 编辑视图**不**这么做：那里要看清"我选的是什么颜色"。
       * 两个视图各有各的职责 —— 预览是"结果"，编辑是"意图"。
       * ⚠️ 多色带机器（黑 + 红）不在这一版的承诺里：那需要按色分版，
       * 属于输出侧的事（架构文档 §5.1）。
       * ══════════════════════════════════════════════════════════════════
       */
      const isInk = (color: string | undefined): boolean => {
        if (!color) return true
        const hex = color.trim().toLowerCase()
        if (hex === 'none' || hex === 'transparent') return false
        if (hex === '#fff' || hex === '#ffffff' || hex === 'white') return false
        const rgb = hex.match(/rgba?\(([^)]+)\)/)
        if (rgb) {
          const [r, g, b, a] = rgb[1]!.split(',').map((part) => Number(part.trim()))
          /* 全白 或 全透明 → 不上墨 */
          if ((a ?? 1) === 0) return false
          if (r === 255 && g === 255 && b === 255) return false
        }
        return true
      }
      /** 上墨 = 黑（单色机的唯一一种墨） */
      const inkOf = (color: string | undefined): string => (isInk(color) ? '#000000' : 'none')

      const children: VNode[] = []
      /*
       * ★ **圆角裁剪**：IR 里带 `clip` 的 op 按组件分组，外面包一层
       * `<g clip-path>`（`<clipPath>` 里是一个圆角矩形）。
       *
       * ⚠️ 走 IR 的 `clip` 而不是"按组件自己算"：预览画的必须是**执行侧
       * 会画的东西** —— 那份数据就是契约（`PrintClip`）。
       */
      const clipDefs: VNode[] = []
      const clipIdOf = (clip: { xDots: number; yDots: number; wDots: number; hDots: number; radiusDots: number }, index: number) => {
        const id = `npp-clip-${index}`
        clipDefs.push(
          h('clipPath', { id }, [
            h('rect', {
              x: DOT(clip.xDots),
              y: DOT(clip.yDots),
              width: DOT(clip.wDots),
              height: DOT(clip.hDots),
              rx: DOT(clip.radiusDots),
            }),
          ]),
        )
        return id
      }
      /*
       * **图片的二值化滤镜**（真机反馈："放一张图就全是黑色的了"）。
       *
       * 做法：`feColorMatrix` 取亮度 → `feComponentTransfer` 用**离散**两档
       * （`tableValues` 的前一半 → 0（不上墨），后一半 → 1（上墨））。
       * 阈值就是 op 的 `threshold`（0–255）。
       *
       * ⚠️ 为什么用 SVG 滤镜而不是 canvas：无头环境里 canvas 拿不到上下文
       * （那块就永远测不到），而滤镜是**声明式**的 —— DOM 里看得见、能断言，
       * 而且浏览器自己算，与设备无关。
       */
      const thresholdFilters: VNode[] = []
      const ensureThresholdFilter = (threshold: number): string => {
        const id = thresholdFilterId(threshold)
        if (thresholdFilters.some((node) => node.key === id)) return id
        thresholdFilters.push(thresholdFilterNode(id, threshold))
        return id
      }
      let clipSeq = 0
      /* 一个组 = 同一个组件 + 同一个 clip（同一个组件只可能有一种 clip） */
      let groupOps: VNode[] = []
      let groupClip: string | null = null
      const flushGroup = () => {
        if (groupOps.length === 0) return
        /*
         * ⚠️ 这一行必须是 `children.push`：整段 `groupOps.push` 的批量替换
         * 把它也换掉了，于是"组永远推不回 children" —— 预览一片空白，
         * 而代码看起来完全正常（这个 bug 只有渲染出来才看得见）。
         */
        children.push(
          groupClip
            ? h('g', { 'data-npp-clip-group': groupClip, 'clip-path': `url(#${groupClip})` }, groupOps)
            : h('g', {}, groupOps),
        )
        groupOps = []
        groupClip = null
      }

      for (const op of tree.ops) {
        const wantedClip = op.clip ? clipIdOf(op.clip, clipSeq++) : null
        if (wantedClip !== groupClip) {
          flushGroup()
          groupClip = wantedClip
        }
        if (op.op === 'rect') {
          groupOps.push(
            h('rect', {
              'data-npp-op': 'rect',
              'data-invert': String(op.invert),
              x: DOT(op.xDots),
              y: DOT(op.yDots),
              width: DOT(op.wDots),
              height: DOT(op.hDots),
              rx: op.radiusDots > 0 ? DOT(op.radiusDots) : undefined,
              /*
               * ⚠️ 颜色来自 op（IR 是真源），再过一道**二值化**：
               * 白 = 不上墨（`none`），别的颜色 = 墨（黑）。
               */
              fill: op.invert ? inkOf(op.strokeColor) : op.fill ? inkOf(op.fillColor) : 'none',
              /*
               * ★★ **边框画不画只看 `lineWidthDots`，不看 `fill`**。
               *
               * IR 的类型注释写着「线宽（点）；0 = **只填充不描边**」—— 反过来
               * 就是"非 0 就要描边，填充开着也要"。原来这里写的是
               * `op.lineWidthDots > 0 && !op.fill`：填充一打开，整条边框**消失**，
               * 而编辑视图照画（真机反馈："编辑模式显示粗，预览就细了"）。
               * 光栅器那边不受影响：1-bit 下边框落在填充区里，本来就分不出来。
               */
              stroke: op.lineWidthDots > 0 ? inkOf(op.strokeColor) : 'none',
              'stroke-width': op.lineWidthDots > 0 ? Math.max(1, DOT(op.lineWidthDots)) : 0,
              transform: rotationAttr(op),
              ...commonAttrs(op),
            }),
          )
        } else if (op.op === 'text') {
          /*
           * 多行 + 垂直对齐：IR 给的是"框顶 + 字号 + 行距 + vAlign"，
           * 这里按同一套规矩摆行 —— **反白时底是黑的、字是白的**
           * （热敏打不出白墨，反白的实现就是"先铺黑、再掏空字"）。
           */
          const lines = op.text.split('\n')
          const lineH = DOT(op.lineHeightDots)
          const blockH = lineH * lines.length
          const boxH = DOT(op.sizeDots) * 1.4
          const top =
            op.vAlign === 'middle'
              ? DOT(op.yDots) + (boxH - blockH) / 2
              : op.vAlign === 'bottom'
                ? DOT(op.yDots) + boxH - blockH
                : DOT(op.yDots)
          if (op.invert) {
            groupOps.push(
              h('rect', {
                'data-npp-op': 'text-invert',
                x: DOT(op.xDots) - 1,
                y: top - 1,
                width: DOT(op.sizeDots) * 0.62 * Math.max(...lines.map((line) => line.length)) + 2,
                height: blockH + 2,
                fill: op.color,
                transform: rotationAttr(op),
              }),
            )
          }
          lines.forEach((line, index) => {
            groupOps.push(
              h(
                'text',
                {
                  'data-npp-op': 'text',
                  'data-line': String(index),
                  'data-advance': String(op.advanceDots),
                  /*
                   * ★ **落点只用冻好的数字算**（`advanceDots` + `boxWDots`），
                   * 不再让浏览器用 `text-anchor` 自己量宽度 ——
                   * 各端测量实现不同（Canvas2D vs Skia），那正是架构 §4.2
                   * 要根掉的那类偏差：编辑器里居中、打出来偏一点。
                   *
                   * ⚠️ 多行的居中仍然按**整框**算（每行同一个 x）——
                   * 逐行居中需要每行各自的宽度，而 IR 只冻了最长那一行
                   * （换行/断词还没做，见 `measure.ts` 的边界说明）。
                   */
                  x: DOT(
                    op.align === 'center'
                      ? op.xDots + (op.boxWDots - op.advanceDots) / 2
                      : op.align === 'right'
                        ? op.xDots + op.boxWDots - op.advanceDots
                        : op.xDots,
                  ),
                  y: top + lineH * index + DOT(op.sizeDots),
                  'font-size': DOT(op.sizeDots),
                  'font-weight': op.bold ? '700' : '400',
                  'text-anchor': 'start',
                  /* 反白 = 字掏空（纸色）+ 底下铺墨（由 rect 那条负责） */
                  fill: op.invert ? '#ffffff' : inkOf(op.color),
                  'font-family': 'sans-serif',
                  transform: rotationAttr(op),
                  ...commonAttrs(op),
                },
                line,
              ),
            )
          })
        } else if (op.op === 'image') {
          groupOps.push(
            op.src
              ? h('image', {
                  'data-npp-op': 'image',
                  href: op.src,
                  x: DOT(op.xDots),
                  y: DOT(op.yDots),
                  width: DOT(op.wDots),
                  height: DOT(op.hDots),
                  preserveAspectRatio:
                    op.fit === 'fill'
                      ? 'none'
                      : op.fit === 'cover'
                        ? 'xMidYMid slice'
                        : 'xMidYMid meet',
                  transform: rotationAttr(op),
                  /* ★ 二值化：按 `threshold` 把图压成"上墨 / 不上墨" */
                  filter: `url(#${ensureThresholdFilter(op.threshold)})`,
                  'data-npp-threshold': String(op.threshold),
                })
              : h('rect', {
                  'data-npp-op': 'image',
                  'data-npp-image-empty': 'true',
                  x: DOT(op.xDots),
                  y: DOT(op.yDots),
                  width: DOT(op.wDots),
                  height: DOT(op.hDots),
                  fill: 'none',
                  stroke: '#bbb',
                  'stroke-dasharray': '3 3',
                  transform: rotationAttr(op),
                }),
          )
        } else if (op.kind === '1d') {
          /*
           * ⚠️ 几何在 IR 里是**设备点**，先折成画布 px 再交给 `pathData.ts` ——
           * 「一个模块 = 多少画布单位」在两个视图里不一样（编辑视图按物理尺寸、
           * 预览按整数点），但**拼 `d` 的规矩只有一处**。
           */
          const d = barsPathData({
            bars: op.bars.map((bar) => ({ x: bar.xDots, w: bar.wDots })),
            originX: 0,
            originY: DOT(op.yDots),
            moduleSize: DOT(1),
            height: DOT(op.heightDots),
          })
          groupOps.push(
            h('path', {
              'data-npp-op': 'barcode-1d',
              d,
              /* 墨色 = op 的 `color`，二值化之后 = 黑或不上墨 */
              fill: inkOf(op.color),
              transform: rotationAttr(op),
              ...commonAttrs(op),
            }),
          )
        } else if (op.kind === '2d') {
          /*
           * ★ 这里原来是 `d: parts`（子路径**数组**）—— Vue 会隐式 `join(',')`，
           * 于是命令之间多出逗号，而 SVG 的路径语法不允许（只有参数之间能有逗号）：
           * 浏览器报 `Error: Problem parsing d="M356.65 173.08h22.03v3"`，
           * 二维码在预览里一片空白（条码 1d 那份顺手 join 了，所以只有二维码犯病）。
           *
           * 现在 `matrixPathData` 返回字符串 —— 它内部走的是**同一个** `pathDataOf`。
           */
          const d = matrixPathData({
            rows: op.rows,
            cols: op.cols,
            ink: (row, col) => op.matrix[row]?.[col] === '1',
            originX: DOT(op.xDots),
            originY: DOT(op.yDots),
            moduleSize: DOT(op.moduleDots),
          })
          groupOps.push(
            h('path', {
              'data-npp-op': 'barcode-2d',
              d,
              fill: inkOf(op.color),
              transform: rotationAttr(op),
              ...commonAttrs(op),
            }),
          )
        }
      }
      /* 最后一组收尾（循环里只在"组变了"时 flush） */
      flushGroup()
      /*
       * ★★ **预览也长在"一张纸的外壳"里**（`renderSheetChrome`）——
       * 标尺、标签、壳尺寸和编辑视图**同一份实现**（真机反馈："编辑模式和预览
       * 模式切换的时候，为什么标尺会消失"）。
       *
       * ⚠️ 结构是 `.npp__preview`（flex 列）→ 里面第一层是 `.npp__sheet`，
       * 问题清单在它**下面**（不在壳里：问题有几条是不确定的，壳的高度算不出来）。
       */
      return h(
        'div',
        {
          class: cls('preview'),
          'data-npp-preview-page': tree.pageId,
          'data-npp-preview-key': page.key,
          'data-preview-background': background,
        },
        [
          /*
           * ★★ **纸的像素尺寸按 mm 算，不按"点 → mm"算回去**（2027 修）。
           *
           * 以前这里是"设备点 → mm → px"（`toPx(tree.widthDots)`）：60mm 先取整
           * 成 480 点，再换回来变成 60.0591mm —— 于是**预览的纸比编辑视图的纸
           * 宽 0.1%**（254.995px vs 254.77px）。那点差在屏幕上是半个像素，但它让"两个视图
           * 的纸不是同一张纸"，也把里面每个元素的位置整体挪了一点点。
           *
           * 现在：**外框用 mm（和编辑视图同一个数），内部坐标仍用设备点**
           * （viewBox 是点，SVG 自己把这 0.1% 缩放掉）——
           * 几何的真源还是 IR，屏幕上两张纸则严丝合缝地对齐。
           */
          renderSheetChrome(
            page,
            zoomValue,
            mmToPx(page.widthMm, zoomValue),
            mmToPx(page.heightMm, zoomValue),
            [
            /*
             * ★ **网格 + 边距在预览里也有**（2027，用户："预览和编辑模式的显示效果
             * 不一样"里最扎眼的一条）。它们是**视图开关**（工具栏那两个按钮），
             * 画的是**纸的属性** —— 和标尺同一类，见 `renderSheetOverlays`。
             * ⚠️ 顺序：在 SVG **之前**（网格是底，不压内容）。
             */
            ...renderSheetOverlays(page, zoomValue),
            h(
              'svg',
              {
                class: cls('preview-svg'),
                /*
                 * ★ **给验证脚本留的只读钩子**：把这一屏画的 IR 挂成 DOM 节点的
                 * 一个 JS 属性（**不进 DOM 属性** —— 一份 IR 几万字符，塞进 markup
                 * 会让页面本身变胖）。
                 *
                 * 为什么需要它：S5 的"三方对拍"要的是"**同一份 IR** 的三个消费者"
                 * —— 预览（SVG）、光栅器（纯算术）、IR 自己。没有这个钩子，
                 * 端到端里就只能各画各的，那种"一致"是假的。
                 * ⚠️ 它只是**引用**，脚本读它不会影响渲染。
                 */
                ref: (el: unknown) => {
                  const node = el as SVGSVGElement | null
                  if (node) (node as SVGSVGElement & { __nppIr?: PageRenderTree }).__nppIr = tree
                },
                /* ⚠️ CSS 尺寸 = 纸的 mm（和编辑视图一致），viewBox = 设备点（IR 的真源） */
                width: `${mmToPx(page.widthMm, zoomValue)}px`,
                height: `${mmToPx(page.heightMm, zoomValue)}px`,
                viewBox: `0 0 ${tree.widthDots} ${tree.heightDots}`,
                'shape-rendering': 'crispEdges',
                /*
                 * ⚠️ 底色是**介质模拟**（`page.backgroundColor`，仅预览）——
                 * 它**不在 IR 里**：打印机只知道"哪里上墨"，不知道纸什么颜色。
                 * 预览把它画出来，是为了让人看清"这个版面上黑纸/彩色纸好不好看"。
                 */
                style: { background: background },
                'data-preview-background': background,
              },
              /*
               * ⚠️ `clipPath` / 二值化滤镜的定义都要在 `defs` 里（引用走 `url(#id)`）
               */
              clipDefs.length > 0 || thresholdFilters.length > 0
                ? [h('defs', {}, [...clipDefs, ...thresholdFilters]), ...children]
                : children,
            ),
          ]),
          ...tree.problems.map((problem) =>
            h(
              'div',
              {
                class: cls(`preview-problem is-${problem.severity}`),
                'data-npp-problem': problem.severity,
                'data-npp-problem-el': problem.elementId ?? '',
              },
              problem.message,
            ),
          ),
        ],
      )
    }

    /**
     * 页面级的**问题横幅**（编辑视图也显示 —— 压在边距上这种事，
     * 编辑时就该看见，不该等到"导出前检查"）。
     */
    /** 浮层里那份问题清单展开着吗（**视图状态**：不进文档） */
    const problemsOpen = ref(false)

    /**
     * **问题提示**（浮层）。
     *
     * ⚠️ 真机反馈："错误信息用提示显示，而不是直接显示一个区域 ——
     * 它的显示和隐藏会导致画布区域移动"。所以它是 `position: absolute` 的浮层，
     * 出现 / 消失都不改变纸的位置；点一下才展开完整清单（默认只报个数）。
     */
    /**
     * **把这一批问题交给宿主**（输出窗口里统一看）。
     *
     * ⚠️ 只在**内容变了**的时候报：`publishDiagnostics` 会写宿主的响应式状态，
     * 而编辑器又跟着重渲染 —— 每帧都报就是自己把自己转起来（无限循环）。
     * 比对用序列化后的字符串，够用且直观。
     */
    let lastReported = ''
    function publishProblems(file: ReturnType<typeof compileToFile>): void {
      const report = props.publishDiagnostics
      if (!report) return
      const items = file.pages.flatMap((page) =>
        page.problems.map((problem) => ({
          severity: problem.severity,
          message: `${page.pageId ? '' : ''}${problem.message}`,
          ...(problem.elementId ? { nodeId: problem.elementId } : {}),
        })),
      )
      const key = JSON.stringify(items)
      if (key === lastReported) return
      lastReported = key
      report(props.area.id, items)
    }

    function renderProblemBar(): VNode | null {
      const file = compileToFile(pages.value, jobOf())
      /* ★ 顺手把这一批问题**交给宿主**（输出窗口里统一看） */
      publishProblems(file)
      const counts = problemCounts(file)
      if (counts.errors === 0 && counts.warnings === 0) return null
      const problems = file.pages.flatMap((page) => page.problems)
      /*
       * ★ **感叹号徽标**（真机反馈："可以给页面添加一些图标标记，比如红色感叹、
       * 黄色三角感叹、灰色信息感叹，这样有问题的时候，点击感叹号就可以打开
       * 一个输出窗口"）。
       *
       * ⚠️ 严重度取**最坏的那一档** + 各自的条数：一个"1/0"的数字读不出
       * "这是错误还是提醒"，而图标一眼就能读。
       */
      const worst = counts.errors > 0 ? 'error' : counts.warnings > 0 ? 'warning' : 'info'
      const label =
        counts.errors > 0
          ? `${counts.errors} 个错误${counts.warnings > 0 ? `、${counts.warnings} 个提醒` : ''}`
          : counts.warnings > 0
            ? `${counts.warnings} 个提醒`
            : '没有问题'
      return h('div', { class: cls('problems'), 'data-npp-problems': `${counts.errors}/${counts.warnings}` }, [
        h(
          'button',
          {
            class: [cls('badge'), cls(`badge--${worst}`)],
            type: 'button',
            'data-npp-action': 'problems-open-output',
            'data-npp-severity': worst,
            title: `${label} —— 点开在「输出」窗口里看详情`,
            /* ⚠️ 点它是**打开输出窗口**，不是就地展开：详情要统一在一处看 */
            onClick: () => props.openOutput?.(),
          },
          [
            /* 图标：错误 = 实心感叹，提醒 = 三角感叹，信息 = i */
            h(
              'svg',
              {
                width: 12,
                height: 12,
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                'stroke-width': 2,
                'stroke-linecap': 'round',
                'aria-hidden': 'true',
              },
              [
                h('path', {
                  d:
                    worst === 'warning'
                      ? 'M12 4l9 16H3zM12 10v4M12 17h.01'
                      : worst === 'info'
                        ? 'M12 8h.01M11 12h1v5h1'
                        : 'M12 7v6M12 17h.01',
                }),
              ],
            ),
            h('span', { class: cls('badge-text') }, label),
          ],
        ),
        /*
         * 就地展开的那一份**留着**（不想离开这个窗口时用），但：
         *   · 默认收起；
         *   · 展开时**够高**（真机反馈："详情高度太窄了，看不见"）。
         */
        h(
          'button',
          {
            class: 'npp-btn',
            type: 'button',
            'data-npp-action': 'problems-toggle',
            style: { pointerEvents: 'auto' },
            onClick: () => {
              problemsOpen.value = !problemsOpen.value
            },
          },
          problemsOpen.value ? '收起' : '就地看',
        ),
        problemsOpen.value
          ? h(
              'div',
              { class: cls('problem-list'), style: { pointerEvents: 'auto' } },
              problems.map((problem) =>
                h(
                  'div',
                  { class: cls('problem-item'), 'data-npp-problem': problem.severity, 'data-npp-problem-el': problem.elementId ?? '' },
                  problem.message,
                ),
              ),
            )
          : null,
      ])
    }

    return () => {
      const zoomValue = zoom.value
      const state = view.value
      const list = pages.value
      const page = activePage.value

      /*
       * 「编辑 / 预览」——两档视图（架构文档 §1.3：不许混成一个）。
       * 预览档里那种"1 点 = 1px"的缩放是**打印域特有**的一档：
       * 203dpi 的一个点只有 0.125mm，按物理尺寸看是半个屏幕像素 ——
       * 在编辑视图里根本看不见"条宽是 3 个点还是 4 个点"。
       */
      /*
       * ⚠️ 视图档位（编辑 / 预览）与缩放**搬到了窗口头部**
       * （`EditorContribution.header` → `PrintHeaderControls`）：
       * 这一排工具栏只干一件事 —— **往纸上加东西**。
       * 真机反馈原话："页面编辑器中有很多的按钮，这一部分可以放在窗口头部"。
       */
      /*
       * ⚠️ 二级行也是**可横滚的条**（内核那条能力，`createStripScroll`）：
       * 窄窗口下按钮不许换行 / 被压扁（真机反馈）。
       */
      const bar = h(
        'div',
        { ref: barEl, class: [cls('bar'), 'np-strip'], onWheel: stripScroll.onWheel },
        [
        /*
         * ⚠️ 这一排只干一件事：**往纸上加东西**，而且全是**图标按钮**
         * （真机反馈："按钮组需要做成图标按钮"）。缩放 / 视图档位在窗口头部。
         */
        button('加文本', 'add-text', () => addComponent('text'), {
          icon: TOOL_ICONS.text,
          accent: true,
          title: '加文本',
        }),
        button('加矩形', 'add-box', () => addComponent('box'), {
          icon: TOOL_ICONS.box,
          accent: true,
          title: '加矩形',
        }),
        button('加条码', 'add-barcode', () => addComponent('barcode'), {
          icon: TOOL_ICONS.barcode,
          accent: true,
          title: '加条码',
        }),
        button('加二维码', 'add-qrcode', () => addComponent('qrcode'), {
          icon: TOOL_ICONS.qrcode,
          accent: true,
          title: '加二维码',
        }),
        button('加图片', 'add-image', () => addComponent('image'), {
          icon: TOOL_ICONS.image,
          accent: true,
          title: '加图片',
        }),
        h('span', { class: 'npp-sep' }),
        /*
         * 网格 / 吸附 / 标尺留在这儿（它们**不是**"看哪儿"，而是"摆东西时的规矩"），
         * 但只有文字标签那一项保留文字，其余是开关按钮。
         */
        h('label', { class: 'npp-field', title: '网格步长（0 = 关闭）' }, [
          h('span', { class: 'npp-field__label' }, '网格'),
          h(
            'select',
            {
              class: 'npp-input',
              'data-npp-action': 'grid',
              value: String(state.gridMm),
              onChange: (ev: Event) => {
                state.gridMm = Number((ev.target as HTMLSelectElement).value)
              },
            },
            GRID_CHOICES.map((mm) => h('option', { value: String(mm) }, mm === 0 ? '关' : `${mm}mm`)),
          ),
        ]),
        button('吸附', 'snap', () => {
          state.snap = !state.snap
        }, { active: state.snap, title: '吸附：网格与组件边缘 / 中线' }),
        button('标尺', 'rulers', () => {
          state.rulers = !state.rulers
          },
          { active: state.rulers, title: '显示标尺' },
        ),
        ],
      )

      /**
     * **滚轮缩放**（以指针位置为锚点）。
     *
     * ⚠️ 锚点必须自己算：`scrollLeft` 是"视口左上角在内容里的位置"，
     * 缩放后要让它落在**同一个纸上的点**上，否则"放大之后纸往右下跑"。
     * 这也是"放大后左边看不到"的同源问题（见样式里 `width: max-content` 那段）。
     */
    function onWheel(ev: WheelEvent): void {
      const host = scroller.value
      if (!host) return
      ev.preventDefault()
      const rect = host.getBoundingClientRect()
      const localX = ev.clientX - rect.left
      const localY = ev.clientY - rect.top
      const before = host.scrollLeft + localX
      const beforeY = host.scrollTop + localY
      const factor = ev.deltaY < 0 ? 1.12 : 1 / 1.12
      const current = zoom.value
      /* ⚠️ `zoom` 是 computed（它要把 'fit' 解析成数值），写的是 `state.zoom` */
      const next = clamp(zoom.value * factor, MIN_ZOOM, MAX_ZOOM)
      state.zoom = next
      void Promise.resolve().then(() => {
        const scale = next / current
        host.scrollLeft = before * scale - localX
        host.scrollTop = beforeY * scale - localY
      })
    }

    /** 双指捏合（移动端）：两指距离的比例就是缩放比例 */
    let pinch: { distance: number; zoom: number } | null = null
    function onTouchMove(ev: TouchEvent): void {
      if (ev.touches.length !== 2) {
        pinch = null
        return
      }
      ev.preventDefault()
      const [a, b] = [ev.touches[0]!, ev.touches[1]!]
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
      if (!pinch) {
        pinch = { distance, zoom: zoom.value }
        return
      }
      view.value.zoom = clamp((pinch.zoom * distance) / Math.max(1, pinch.distance), MIN_ZOOM, MAX_ZOOM)
    }

    const body = list.length
        ? h(
            'div',
            {
              class: cls('scroll'),
              ref: scroller,
              onWheel,
              onTouchmove: onTouchMove,
              onTouchend: () => {
                pinch = null
              },
            },
            state.mode === 'preview'
              ? h(
                  'div',
                  { class: cls('pages') },
                  list.map((item) => renderPreview(compilePage(item), zoomValue, item)),
                )
              : h('div', { class: cls('pages') }, list.map((item) => renderSheet(item, zoomValue))),
          )
        : h('div', { class: cls('scroll'), ref: scroller }, [
            h('div', { class: cls('empty') }, [
              h('strong', '还没有页面'),
              /* ⚠️ 一句就够：讲"怎么开始"，不讲"这个功能是怎么设计的" */
              h('span', '建一个页面就能往上摆组件'),
              button('新建页面', 'create-page', () => createPage(), { accent: true }),
            ]),
          ])

      return h(
        'div',
        {
          class: PRINT_EDITOR_CLASS,
          'data-print-editor': props.area.id,
          'data-zoom': String(Math.round(zoomValue * 1000) / 1000),
          /* 纸的底色（第一页）——验证脚本读它，不用去反解样式 */
          'data-background': list[0]?.backgroundColor ?? '',
          'data-npp-drag': dragMode.value,
          'data-npp-mode': state.mode,
          'data-page-count': String(list.length),
          'data-active-page': page?.id ?? '',
        },
        /*
         * ★ **二值化滤镜的定义**（编辑视图也要）。
         *
         * ⚠️ 编辑视图里的 `<img>` 引用了 `url(#npp-threshold-…)` —— 那个定义
         * 必须真的存在：SVG 里引用一个**不存在的滤镜**不会"什么都不做"，
         * 而是让元素**不渲染**（各家浏览器略有差别，但都不是"照原样画"）。
         * 精确预览那一份定义在它自己的 `<defs>` 里；编辑视图这一份在这里。
         */
        [bar, renderProblemBar(), renderThresholdDefs(list), body],
      )
    }
  },
})

export { PrintPageEditor }
